// src/pages/MemberManagement.jsx
import React from "react";
import { Plus, ChevronDown, UserCog, Pencil, Trash2, Eye, UserCheck, UserX, Mail, Phone, FileSpreadsheet, Building2 } from "lucide-react";
import { clsx } from "clsx";
import { useMemberManagePage } from "@hooks";
import { MemberFormModal } from "./MemberFormModal"; 
import { MemberViewModal } from "./MemberViewModal"; 
import { PageHeader, FilterBar, DataTable, ExcelUploadModal } from "@components"; // 🌟 공통 컴포넌트 임포트


const STATUSES = [
  { value: "전체", label: "전체" },
  { value: "A", label: "정상" },
  { value: "B", label: "정지" },
  { value: "C", label: "삭제" }
];

const STATUS_STYLE = { A: "bg-green-50 text-green-700", B: "bg-amber-50 text-amber-700", C: "bg-slate-100 text-slate-500" };
const STATUS_LABEL = { A: "정상", B: "정지", C: "삭제" };
const STATUS_ICON = { A: <UserCheck className="h-3 w-3" />, B: <UserX className="h-3 w-3" />, C: <UserX className="h-3 w-3" /> };

export function MemberManagement() {
  const { 
    filters, setFilters, page, onSearch, modal, setModal, 
    isExcelOpen, setIsExcelOpen, uploadExcel, isUploadingExcel, downloadTemplate, // 🌟 엑셀 관련 훅 꺼내기
    createMember, updateMember, deleteMember, isCreating, isUpdating, auth
  } = useMemberManagePage();

  const handleReset = () => {
    setFilters({
      branch: '',
      status: '전체',
      searchKeyword: '',
      isAdminChecked: false, // 관리자만 보기 체크박스 상태
      pageNum: 1 // 페이지도 1페이지로 리셋
    });
  };

  const handleDelete = async (memberId) => {
    if (window.confirm("해당 회원을 삭제(정지) 처리하시겠습니까?")) await deleteMember(memberId);
  };

  // 🌟 (추가) 회원 관리 초기 도움말
  const initialHelpText = `수행요원 및 관리자 등 포털 시스템을 이용하는 '회원 계정'을 관리하는 페이지입니다.

[주요 기능 안내]
1. 회원 등록: 우측 상단의 [회원 등록] 버튼을 눌러 새로운 사용자의 접속 계정을 생성합니다.
2. 상세 조회: 표 우측 관리 탭의 눈 모양(👁️) 아이콘을 누르면 회원의 상세 정보와 권한을 확인할 수 있습니다.
3. 정보 수정: 연필 모양(✏️) 아이콘을 눌러 소속, 역할(권한), 비밀번호 초기화 등의 작업을 수행합니다.
4. 계정 삭제(정지): 쓰레기통 모양(🗑️) 아이콘을 누르면 해당 회원의 접속이 차단(상태 C: 삭제)됩니다.`;

  // 🌟 테이블 컬럼 정의
  const tableColumns = [
    { label: "아이디" },
    { label: "성명" },
    { label: "역할" },
    { label: "소속" },
    { label: "연락처", className: "min-w-[140px]" },
    { label: "이메일", className: "min-w-[180px]" },
    { label: "등록일", align: "text-center" },
    { label: "배정 업체", align: "text-right" },
    { label: "상태" },
    { label: "관리", align: "text-center" }
  ];

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      
      <PageHeader 
        icon={UserCog} 
        iconBgColor="bg-indigo-600"
        title="회원 관리" 
        description="수행요원 및 관리자 등 포털 이용 회원을 관리합니다." 
        auth={auth} 
        helpId="member_management"
        initialHelpText={initialHelpText}
      />

      <FilterBar
        onReset={handleReset} 
        actions={
          <>
            <button onClick={() => setModal({ type: 'add', member: null })} className="flex h-10 items-center gap-2 rounded-lg bg-indigo-600 px-4 text-sm font-medium text-white hover:bg-indigo-700 transition-colors">
              <Plus className="h-4 w-4" /> 회원 등록
            </button>
            {/* 🌟 엑셀 업로드 버튼 추가 */}
            <button onClick={() => setIsExcelOpen(true)} className="flex h-10 items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors">
              <FileSpreadsheet className="h-4 w-4 text-green-600" /> 엑셀 업로드
            </button>
          </>
        }
      >
        <div className="space-y-1.5 w-32">
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">계정상태</label>
          <div className="relative">
            <select value={filters.status} onChange={(e) => { setFilters(p => ({ ...p, status: e.target.value })); onSearch(); }} className="h-10 w-full appearance-none rounded-lg border border-slate-300 bg-white px-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500">
              {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 pointer-events-none" />
          </div>
        </div>

        <div className="space-y-1.5 flex-1 min-w-[150px] max-w-[200px]">
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">소속</label>
          <div className="relative">
            <input 
              type="text" 
              value={filters.branch} 
              onKeyDown={(e) => e.key === 'Enter' && onSearch()} 
              onChange={(e) => setFilters(p => ({ ...p, branch: e.target.value }))} 
              placeholder="소속명 검색" 
              className="h-10 w-full rounded-lg border border-slate-300 bg-white pl-9 pr-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500" 
            />
            <Building2 className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          </div>
        </div>

        <div className="space-y-1.5 flex-1 min-w-[150px] max-w-[250px]">
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">검색</label>
          <input type="text" value={filters.searchKeyword} onKeyDown={(e) => e.key === 'Enter' && onSearch()} onChange={(e) => setFilters(p => ({ ...p, searchKeyword: e.target.value }))} placeholder="성명/ID/이메일" className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500" />
        </div>
         
        <label className="flex items-center gap-2 h-10 px-3 rounded-lg border border-slate-200 bg-slate-50 cursor-pointer hover:bg-slate-100 transition-colors mb-0 lg:mb-0">
          <input type="checkbox" checked={filters.isAdminChecked} onChange={(e) => { setFilters(p => ({ ...p, isAdminChecked: e.target.checked })); onSearch(); }} className="w-4 h-4 text-indigo-600 rounded border-slate-300 focus:ring-indigo-500" />
          <span className="text-sm font-medium text-slate-700 select-none">관리자만 조회</span>
        </label>
      </FilterBar>

      <DataTable 
        columns={tableColumns}
        page={page}
        itemName="회원 데이터"
        onPageChange={(newPage) => setFilters(p => ({ ...p, pageNum: newPage }))}
        emptyMessage="검색된 회원이 없습니다."
        renderRow={(member) => (
          <tr key={member.memberId} className="group hover:bg-slate-50/80 transition-colors">
            <td className="px-4 py-3.5 font-mono text-xs text-slate-500">{member.memberId}</td>
            <td className="px-4 py-3.5 font-medium text-indigo-700 cursor-pointer hover:underline" onClick={() => setModal({ type: 'view', member })}>{member.memberNm}</td>
            <td className="px-4 py-3.5">
              <span className={clsx("inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium", member.roleId === 'R0' ? "bg-purple-50 text-purple-700" : "bg-teal-50 text-teal-700")}>{member.roleName}</span>
            </td>
            <td className="px-4 py-3.5 text-slate-600">{member.branchName || '-'}</td>
            <td className="px-4 py-3.5">
              {member.phone && member.phone !== '--' ? (
                <a href={`tel:${member.phone}`} className="flex items-center gap-1.5 text-slate-600 hover:text-indigo-600 transition-colors"><Phone className="h-3.5 w-3.5 text-slate-400" />{member.phone}</a>
              ) : <span className="text-slate-400">-</span>}
            </td>
            <td className="px-4 py-3.5">
              {member.email ? (
                <a href={`mailto:${member.email}`} className="flex items-center gap-1.5 text-slate-600 hover:text-indigo-600 transition-colors"><Mail className="h-3.5 w-3.5 text-slate-400" />{member.email}</a>
              ) : <span className="text-slate-400">-</span>}
            </td>
            <td className="px-4 py-3.5 text-center text-slate-500">{member.regDtm}</td>
            <td className="px-4 py-3.5 text-right font-medium text-slate-700">{member.roleId === 'R0' ? '-' : `${member.assignedCount}개`}</td>
            <td className="px-4 py-3.5">
              <span className={clsx("inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium", STATUS_STYLE[member.status] || STATUS_STYLE.A)}>
                {STATUS_ICON[member.status] || STATUS_ICON.A} {STATUS_LABEL[member.status] || "정상"}
              </span>
            </td>
            <td className="px-4 py-3.5 text-center">
              <div className="flex items-center justify-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={() => setModal({ type: 'view', member })} title="상세" className="p-1.5 rounded text-slate-400 hover:text-indigo-600 hover:bg-indigo-50"><Eye className="h-4 w-4" /></button>
                <button onClick={() => setModal({ type: 'edit', member })} title="수정" className="p-1.5 rounded text-slate-400 hover:text-amber-600 hover:bg-amber-50"><Pencil className="h-4 w-4" /></button>
                <button onClick={() => handleDelete(member.memberId)} title="삭제" className="p-1.5 rounded text-slate-400 hover:text-red-600 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
              </div>
            </td>
          </tr>
        )}
      />

      {/* Modals */}
      {modal.type === "view" && <MemberViewModal member={modal.member} onClose={() => setModal({ type: null, member: null })} onEdit={() => setModal({ type: 'edit', member: modal.member })} />}
      {(modal.type === "add" || modal.type === "edit") && (
        <MemberFormModal type={modal.type} member={modal.member} onClose={() => setModal({ type: null, member: null })} onSubmit={modal.type === "add" ? createMember : updateMember} isSubmitting={modal.type === "add" ? isCreating : isUpdating} />
      )}

      <ExcelUploadModal 
        isOpen={isExcelOpen} 
        onClose={() => setIsExcelOpen(false)} 
        onUpload={(file) => uploadExcel(file).then(() => alert('엑셀 업로드가 완료되었습니다.')).catch(e => alert('업로드 실패: ' + (e.response?.data?.message || e.message)))} 
        isUploading={isUploadingExcel} 
        title="회원 계정 엑셀 일괄 등록" 
        description="ID, 성명, 비밀번호, 권한 등이 포함된 회원 목록을 엑셀로 일괄 업로드합니다. 이미 존재하는 ID는 내용이 덮어쓰기 됩니다." 
        onDownloadTemplate={downloadTemplate} 
      />
    </div>
  );
}